import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import {
  Paper,
  Typography,
  Box,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  IconButton,
  alpha,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { createJob, uploadFile } from "../services/api";
import JobHistory from "../components/JobHistory";
import { tokens } from "../theme";

export default function HomePage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [sourceLang, setSourceLang] = useState("auto");
  const [targetLang, setTargetLang] = useState("ja");
  const [outputFormat, setOutputFormat] = useState("md");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((accepted: File[]) => {
    setFiles((prev) => [...prev, ...accepted]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    disabled: submitting,
  });

  const handleRemove = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      setError("画像を 1 枚以上選択してください");
      return;
    }
    setSubmitting(true);
    setError(null);
    setProgress({ current: 0, total: files.length });

    try {
      const { job_id } = await createJob({
        source_lang: sourceLang,
        target_lang: targetLang,
        output_format: outputFormat,
      });

      for (let i = 0; i < files.length; i++) {
        await uploadFile(job_id, files[i], i);
        setProgress({ current: i + 1, total: files.length });
      }

      // 処理は JobStatusPage が client-driven で実行する
      navigate(`/jobs/${job_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      {/* === Hero === */}
      <Box>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          画像から、テキストへ。
        </Typography>
        <Typography variant="body2" color="text.secondary">
          スクリーンショットを OCR し、必要なら翻訳してドキュメントとして書き出します。
        </Typography>
      </Box>

      {/* === Dropzone === */}
      <Paper sx={{ p: 0, overflow: "hidden" }}>
        <Box
          {...getRootProps()}
          sx={{
            p: { xs: 3, md: 6 },
            cursor: submitting ? "not-allowed" : "pointer",
            transition: "all 150ms cubic-bezier(0.4,0,0.2,1)",
            border: `2px dashed ${
              isDragActive ? tokens.accent : tokens.borderStrong
            }`,
            borderRadius: 3,
            m: { xs: 0.75, md: 1 },
            backgroundColor: isDragActive
              ? alpha(tokens.accent, 0.05)
              : "transparent",
            "&:hover": {
              backgroundColor: alpha("#fff", 0.02),
              borderColor: alpha("#fff", 0.18),
            },
          }}
        >
          <input {...getInputProps()} />
          <Stack alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(tokens.accent, 0.1),
                border: `1px solid ${alpha(tokens.accent, 0.2)}`,
              }}
            >
              <CloudUploadOutlinedIcon
                sx={{ color: tokens.accent, fontSize: 24 }}
              />
            </Box>
            <Stack alignItems="center" spacing={0.5} sx={{ textAlign: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {isDragActive
                  ? "ここにドロップ"
                  : "画像をタップ / ドラッグ&ドロップ"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, md: 14 } }}>
                複数可 · PNG / JPG / WebP
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* === Selected files preview === */}
        {files.length > 0 && (
          <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1.5,
                color: "text.secondary",
                fontFamily: "inherit !important",
              }}
            >
              選択中 ({files.length})
            </Typography>
            <Stack spacing={1}>
              {files.map((file, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    border: `1px solid ${tokens.border}`,
                    backgroundColor: alpha("#fff", 0.02),
                  }}
                >
                  <ImageOutlinedIcon
                    sx={{ fontSize: 18, color: "text.secondary" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {(file.size / 1024).toFixed(0)} KB
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(i)}
                    disabled={submitting}
                    sx={{ "&:hover": { color: "error.main" } }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* === Options === */}
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 2,
            color: "text.secondary",
            fontFamily: "inherit !important",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          オプション
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>OCR 言語</InputLabel>
            <Select
              label="OCR 言語"
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
            >
              <MenuItem value="auto">自動検出</MenuItem>
              <MenuItem value="ja">日本語</MenuItem>
              <MenuItem value="en">英語</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>翻訳先</InputLabel>
            <Select
              label="翻訳先"
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
            >
              <MenuItem value="none">翻訳しない</MenuItem>
              <MenuItem value="ja">日本語</MenuItem>
              <MenuItem value="en">英語</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>出力形式</InputLabel>
            <Select
              label="出力形式"
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
            >
              <MenuItem value="md">Markdown</MenuItem>
              <MenuItem value="txt">テキスト</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{
            bgcolor: alpha("#EF4444", 0.08),
            border: `1px solid ${alpha("#EF4444", 0.2)}`,
          }}
        >
          {error}
        </Alert>
      )}

      {submitting && progress.total > 0 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              アップロード中
            </Typography>
            <Typography variant="caption" color="text.primary">
              {progress.current} / {progress.total}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={(progress.current / progress.total) * 100}
          />
        </Box>
      )}

      {/* === Submit === */}
      <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitting || files.length === 0}
          endIcon={<ArrowForwardIcon />}
          sx={{ minWidth: { xs: "100%", sm: 200 }, py: 1.4 }}
        >
          {submitting ? "送信中..." : "OCR + 翻訳を実行"}
        </Button>
      </Box>

      <JobHistory />
    </Stack>
  );
}
